var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _cache, _abortControllersBySectionId, _pendingPromises, _SectionRenderer_instances, abortPendingMorph_fn, cachePageSections_fn;
import { m as morph, M as MORPH_OPTIONS } from "./morph-BX9Iqu9G.js";
import { a as lazyReveal } from "./utilities-2Suih3Nm.js";
import "./component-cLJ9vlHA.js";
import "./critical-Da0Dmhh9.js";
class SectionRenderer {
  constructor() {
    __privateAdd(this, _SectionRenderer_instances);
    /**
     * The cache of section HTML
     * @type {Map<string, string>}
     */
    __privateAdd(this, _cache, /* @__PURE__ */ new Map());
    /**
     * The abort controllers by section ID
     * @type {Map<string, AbortController>}
     */
    __privateAdd(this, _abortControllersBySectionId, /* @__PURE__ */ new Map());
    /**
     * The pending promises
     * @type {Map<string, Promise<string>>}
     */
    __privateAdd(this, _pendingPromises, /* @__PURE__ */ new Map());
    window.addEventListener("load", __privateMethod(this, _SectionRenderer_instances, cachePageSections_fn).bind(this));
  }
  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, url = new URL(window.location.href) } = options ?? {};
    __privateMethod(this, _SectionRenderer_instances, abortPendingMorph_fn).call(this, sectionId);
    const abortController = new AbortController();
    __privateGet(this, _abortControllersBySectionId).set(sectionId, abortController);
    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);
    if (!abortController.signal.aborted) {
      __privateGet(this, _abortControllersBySectionId).delete(sectionId);
      morphSection(sectionId, sectionHTML);
    }
    return sectionHTML;
  }
  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    let pendingPromise = __privateGet(this, _pendingPromises).get(sectionUrl);
    if (pendingPromise) return pendingPromise;
    if (useCache) {
      const cachedHTML = __privateGet(this, _cache).get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }
    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });
    __privateGet(this, _pendingPromises).set(sectionUrl, pendingPromise);
    const sectionHTML = await pendingPromise;
    __privateGet(this, _pendingPromises).delete(sectionUrl);
    __privateGet(this, _cache).set(sectionUrl, sectionHTML);
    return sectionHTML;
  }
}
_cache = new WeakMap();
_abortControllersBySectionId = new WeakMap();
_pendingPromises = new WeakMap();
_SectionRenderer_instances = new WeakSet();
/**
 * Aborts an existing morph for a section
 * @param {string} sectionId - The section ID
 */
abortPendingMorph_fn = function(sectionId) {
  const existingAbortController = __privateGet(this, _abortControllersBySectionId).get(sectionId);
  if (existingAbortController) {
    existingAbortController.abort();
  }
};
/**
 * Caches the page sections
 */
cachePageSections_fn = function() {
  for (const section of document.querySelectorAll(".shopify-section")) {
    const url = buildSectionRenderingURL(section.id);
    if (__privateGet(this, _cache).get(url)) return;
    if (containsShadowRoot(section)) return;
    __privateGet(this, _cache).set(url, section.outerHTML);
  }
};
const SECTION_ID_PREFIX = "shopify-section-";
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set("section_id", normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}
function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}
function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), "");
}
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}
async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, "text/html");
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));
  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }
  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }
  const oldScripts = collectScriptSignatures(existingElement);
  const morphResult = morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    onAfterUpdate(node) {
      var _a, _b;
      (_b = (_a = MORPH_OPTIONS).onAfterUpdate) == null ? void 0 : _b.call(_a, node);
    }
  });
  lazyReveal(morphResult);
  executeNewScripts(existingElement, oldScripts);
}
function collectScriptSignatures(root) {
  var _a, _b;
  const signatures = /* @__PURE__ */ new Set();
  (_b = (_a = root.querySelectorAll) == null ? void 0 : _a.call(root, "script")) == null ? void 0 : _b.forEach((script) => {
    const sig = getScriptSignature(script);
    if (sig) signatures.add(sig);
  });
  return signatures;
}
function getScriptSignature(script) {
  var _a;
  if (script.src) return script.src;
  const text = (_a = script.textContent) == null ? void 0 : _a.trim();
  return text ?? "";
}
function executeNewScripts(root, oldScripts) {
  var _a;
  const scripts = Array.from(((_a = root.querySelectorAll) == null ? void 0 : _a.call(root, "script")) ?? []);
  for (const old of scripts) {
    const sig = getScriptSignature(old);
    if (!sig || oldScripts.has(sig)) continue;
    const fresh = document.createElement("script");
    for (const attr of old.attributes) {
      fresh.setAttribute(attr.name, attr.value);
    }
    fresh.textContent = old.textContent;
    old.replaceWith(fresh);
  }
}
const sectionRenderer = new SectionRenderer();
export {
  morphSection as m,
  sectionRenderer as s
};
